import type {ICompany, PlatformUserStyle, SavedSignatureModel} from "@mytour/shared";
import {isEmptyVatId} from "@mytour/shared";
import {type QueryClient, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useIsAuthenticated} from "../../authentication/authenticationQueries.js";
import {DisplayableError} from "../../services/error.service.js";
import {accountKeysStore} from "./accountQueries.js";
import {platformUserApiService} from "./platformUserApi.service.js";

export const keysStore = {
  root: ["platformUser"] as const,
  current: () => [...keysStore.root, "current"] as const,
  currentStyle: () => [...keysStore.root, "current-style"] as const,
  currentSavedSignature: () => [...keysStore.root, "saved-signature"] as const,
};

export function useCurrentSavedSignature() {
  const {isAuthenticated} = useIsAuthenticated();
  const {data, isLoading, isFetching, isFetchedAfterMount, error, refetch} = useQuery({
    queryKey: keysStore.currentSavedSignature(),
    queryFn: platformUserApiService.getCurrentSavedSignature,
    enabled: isAuthenticated,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });

  return {
    savedSignature: data,
    isSavedSignatureLoading: isLoading,
    isSavedSignatureFetching: isFetching,
    isSavedSignatureFresh: isFetchedAfterMount,
    savedSignatureError: error,
    refetchSavedSignature: refetch,
  };
}

export function useWithdrawSavedSignature() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    async mutationFn(): Promise<SavedSignatureModel> {
      try {
        return await platformUserApiService.withdrawCurrentSavedSignature();
      } catch (error) {
        const current = await platformUserApiService.getCurrentSavedSignature();
        if (current.signature === null) {
          return current;
        }
        throw error;
      }
    },
    onSuccess(model) {
      queryClient.setQueryData(keysStore.currentSavedSignature(), model);
    },
    onError() {
      queryClient.invalidateQueries({queryKey: keysStore.currentSavedSignature()});
    },
  });
  return {withdrawSavedSignature: mutateAsync, isWithdrawingSavedSignature: isPending};
}

export function useCurrentCompany() {
  const {isAuthenticated} = useIsAuthenticated();
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: keysStore.current(),
    queryFn: platformUserApiService.getCurrentCompany,
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  return {
    company: data,
    isCompanyLoading: isLoading,
    isCompanyFetching: isFetching,
    refetchCompany: refetch,
  };
}

/**
 * Whether the current company is VAT-registered (redevable de la TVA) — i.e.
 * it carries a non-empty `legal.vatId`. The single readable signal for "VAT
 * applies", used by the live-TTC previews to decide whether a shifting line
 * gets a rate. Uses `isEmptyVatId` (the same whitespace-aware predicate the BE
 * `#402` guard uses) so the FE and BE agree on what "redevable" means.
 */
export function useIsRedevable(): boolean {
  const {company} = useCurrentCompany();
  return !isEmptyVatId(company?.legal?.vatId);
}

export function useCurrentUserStyle() {
  const {isAuthenticated} = useIsAuthenticated();
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: keysStore.currentStyle(),
    queryFn: platformUserApiService.getCurrentUserStyle,
    // Gated like useAccount/useCurrentCompany: the analytics watcher mounts
    // this hook at the app shell, so an ungated query would fire on /login
    // and its 401 would forceLogout() mid-autologin (interceptor has no
    // already-on-login guard).
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  return {
    platformUserStyle: data,
    isUserStyleLoading: isLoading,
    isUserStyleFetching: isFetching,
    refetchUserStyle: refetch,
  };
}

export function useUpdateInvoicingSettings() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateInvoicingSettings,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: keysStore.root,
      });
      // Setting/clearing the `vatId` (via a full invoicing-settings update) triggers
      // a backend backfill of `service_description.vat_rate`. Refresh the catalog
      // cache so previews built from `useServiceDescriptions` reflect the new VAT
      // regime immediately.
      //
      // Literal key duplicated from `serviceDescriptionKeysStore.root` in
      // `@mytour/service-catalog`. Importing it here would create a package-level
      // cycle (`@mytour/service-catalog` already depends on `@mytour/platform`).
      // Drift is caught by `platformUserQueries.spec.tsx`, which imports the real
      // keysStore from `@mytour/service-catalog` and asserts the call matches —
      // renaming `serviceDescriptionKeysStore.root` without updating this literal
      // fails that spec.
      queryClient.invalidateQueries({queryKey: ["serviceDescription"]});
    },
  });
  return {updateInvoicingSettings: mutateAsync, isUpdatingInvoicingSettings: isPending};
}

export function useUpdateBeforePayment() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateBeforePayment,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: keysStore.root,
      });
    },
  });
  return {updateBeforePayment: mutateAsync, isUpdatingBeforePayment: isPending};
}

export function useUpdateAccountSettings() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateAccountSettings,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: keysStore.root,
      });
      queryClient.invalidateQueries({
        queryKey: accountKeysStore.root,
      });
    },
  });
  return {updateAccountSettings: mutateAsync, isUpdatingAccountSettings: isPending};
}

export function useUpdateCompanySettings() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateCompanySettings,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: keysStore.root,
      });

      queryClient.invalidateQueries({
        queryKey: accountKeysStore.root,
      });
    },
  });
  return {updateCompanySettings: mutateAsync, isUpdatingCompanySettings: isPending};
}

export function useUpdateInvoicingSettingsPartial() {
  const refreshCurrentCompanyAndStyle = useRefreshCurrentCompanyAndStyle();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateInvoicingSettingsPartial,
    async onSuccess() {
      await refreshCurrentCompanyAndStyle();
    },
  });
  return {
    updateInvoicingSettingsPartial: mutateAsync,
    isUpdatingInvoicingSettingsPartial: isPending,
  };
}

export class ReadAfterWriteError extends DisplayableError {
  constructor() {
    super(
      "Impossible de relire les informations de votre société. Vérifiez votre connexion et réessayez."
    );
    this.name = "ReadAfterWriteError";
  }
}

// Un fetch mis en pause par TanStack (réseau tombé en cours de lecture, onglet en arrière-plan) ne se règle jamais seul.
const READ_AFTER_WRITE_TIMEOUT_MS = 30_000;

/**
 * Relit une query exacte qui porte déjà des données, ou rejette (`ReadAfterWriteError`) : jamais un
 * cache périmé rendu comme frais, jamais une attente sans fin. Marquée périmée d'abord pour qu'un
 * échec la laisse invalidée ; sans données, TanStack rejoindrait un fetch antérieur au lieu de le
 * remplacer — les deux appelants garantissent des données (#1233).
 */
async function refetchAfterWrite<T>(
  queryClient: QueryClient,
  queryKey: readonly unknown[]
): Promise<T> {
  await queryClient.invalidateQueries({queryKey, exact: true, refetchType: "none"});
  // L'annulation à l'échéance fait rejeter le refetch, même mis en pause par TanStack après coup.
  const deadline = setTimeout(
    () => queryClient.cancelQueries({queryKey, exact: true}),
    READ_AFTER_WRITE_TIMEOUT_MS
  );
  try {
    await queryClient.refetchQueries({queryKey, exact: true}, {throwOnError: true});
  } catch {
    throw new ReadAfterWriteError();
  } finally {
    clearTimeout(deadline);
  }
  const state = queryClient.getQueryState<T>(queryKey);
  if (!state || state.isInvalidated || state.data === undefined) {
    throw new ReadAfterWriteError();
  }
  return state.data;
}

// Relecture de la société et du style après une écriture.
export function useRefreshCurrentCompanyAndStyle() {
  const queryClient = useQueryClient();
  return function refreshCurrentCompanyAndStyle(): Promise<[ICompany, PlatformUserStyle]> {
    return Promise.all([
      refetchAfterWrite<ICompany>(queryClient, keysStore.current()),
      refetchAfterWrite<PlatformUserStyle>(queryClient, keysStore.currentStyle()),
    ]);
  };
}

/**
 * Société et style pour un document stocké définitivement : le cache tel quel s'il est sain, une
 * relecture stricte seulement s'il est resté marqué périmé (relecture après écriture échouée).
 */
export function useEnsureCurrentCompanyAndStyle() {
  const queryClient = useQueryClient();
  return function ensureCurrentCompanyAndStyle(): Promise<[ICompany, PlatformUserStyle]> {
    return Promise.all([
      ensureFresh<ICompany>(queryClient, keysStore.current()),
      ensureFresh<PlatformUserStyle>(queryClient, keysStore.currentStyle()),
    ]);
  };
}

async function ensureFresh<T>(queryClient: QueryClient, queryKey: readonly unknown[]): Promise<T> {
  const state = queryClient.getQueryState<T>(queryKey);
  if (state && !state.isInvalidated && state.data !== undefined) {
    return state.data;
  }
  return refetchAfterWrite<T>(queryClient, queryKey);
}

export function useUpdateFeature() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: platformUserApiService.updateFeatures,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: keysStore.root,
      });
    },
  });
  return {updateFeature: mutateAsync, isUpdatingFeature: isPending};
}
